import { lazy, Suspense } from "react";
import ReactDOMClient from "react-dom/client";
import { ErrorBoundary } from "react-error-boundary";
import { ToastContainer } from "react-toastify";
import { Tooltip } from "react-tooltip";
import Rollbar from "rollbar";

import Loader from "@components/loader/Loader";
import { WebsiteContextProvider } from "@contexts/WebsiteContext";
import ErrorPage from "@pages/ErrorPage";
import deepFreezeObject from "@utils/helpers/object/deepFreezeObject";
import getAuthenticityToken from "@utils/helpers/document/getAuthenticityToken";

const ROLLBAR_ENABLED_ENVIRONMENTS = ["production"];

const STALE_ASSET_PATTERNS = [
  "Failed to fetch dynamically imported module",
  "is not a valid JavaScript MIME type",
  "Importing a module script failed",
  "Unable to preload CSS",
  "error loading dynamically imported module",
];

const isStaleAssetError = (error) => {
  const message = error?.message || "";
  return STALE_ASSET_PATTERNS.some((pattern) => message.includes(pattern));
};

const loadPageComponent = (componentName) => {
  return lazy(() => {
    return import(`./../javascripts/${componentName}`)
      .then((module) => {
        sessionStorage.removeItem("_kinn_asset_reload");
        return module;
      })
      .catch((error) => {
        if (isStaleAssetError(error) && !sessionStorage.getItem("_kinn_asset_reload")) {
          sessionStorage.setItem("_kinn_asset_reload", "1");
          window.location.reload();
          return new Promise(() => {});
        }

        console.error(error);
        return import("./../javascripts/App");
      });
  });
};

document.addEventListener("DOMContentLoaded", () => {
  const urlParams = new URLSearchParams(window.location.search);

  window.appContext = deepFreezeObject({
    ...document.body.dataset,
    isAuthenticated: document.body.dataset.isAuthenticated === "true",
    isImpersonation: urlParams.has("spoof"),
    authenticityToken: getAuthenticityToken(),
  });

  const isRollbarEnabled = !!window.appContext.rollbarClientToken && ROLLBAR_ENABLED_ENVIRONMENTS.includes(window.appContext.env);

  window.rollbar = new Rollbar({
    accessToken: window.appContext.rollbarClientToken,
    captureUncaught: isRollbarEnabled,
    captureUnhandledRejections: isRollbarEnabled,
    environment: window.appContext.env,
    enabled: isRollbarEnabled,
    payload: {
      custom: {
        appContext: window.appContext,
      },
    },
  });

  /**************************************************************************************************/

  const versionReloadTarget = sessionStorage.getItem("_kinn_version_reload");

  if (versionReloadTarget && versionReloadTarget === window.KINN_PORTALS_VERSION) {
    sessionStorage.removeItem("_kinn_version_reload");
  }

  /**************************************************************************************************/

  console.info(window.KINN_MARK);

  /**************************************************************************************************/

  const appElement = document.getElementById("app");
  if (appElement === null) throw new Error("Missing app element");

  const app = ReactDOMClient.createRoot(appElement);
  const componentName = appElement.dataset.component;
  if (componentName === null) throw new Error("Missing component");

  const Component = loadPageComponent(componentName);
  const props = JSON.parse(appElement.dataset.props);

  app.render(
    <Suspense fallback={<Loader />}>
      <ErrorBoundary FallbackComponent={ErrorPage}>
        <WebsiteContextProvider>
          <Component {...props} />

          <Tooltip
            id="app-tooltip"
            className="tooltip"
            place="bottom"
            delayHide={200}
            opacity={1}
          />
          <ToastContainer theme="dark" />
          <div id="modal-root" />
        </WebsiteContextProvider>
      </ErrorBoundary>
    </Suspense>
  );
});
